import type {
  CandidatoParaDireccion,
  EntrevistaDeDireccion,
} from '@/features/rh/api/vistaDeDireccion'
import { formatDate, ZONA_DE_OPERACION } from '@/shared/lib/dateUtils'

/** Reglas de la vista de Dirección, fuera de la pantalla para poder probarlas. */

const SIN_DATO = '—'

/**
 * Si el candidato llegó sin entrevista de RH.
 *
 * Pasa con las altas manuales: el objeto viene, pero con todo en nulo. Sin
 * esta distinción la ficha mostraría una entrevista vacía como si existiera y
 * hubiera salido en ceros.
 */
export const sinEntrevistaDeRh = (
  entrevista: CandidatoParaDireccion['hrInterview'],
): boolean =>
  !entrevista ||
  (entrevista.score === null &&
    entrevista.comments === null &&
    entrevista.interviewerName === null &&
    entrevista.interviewDate === null)

/** Un texto cualquiera de la ficha, con guion cuando no hay nada. */
export const oGuion = (valor: string | null | undefined): string =>
  valor && valor.trim() !== '' ? valor : SIN_DATO

/**
 * Los montos llegan como número y los vacíos en nulo, nunca en cero: un cero
 * de verdad significa que alguien capturó cero, y se muestra.
 */
export const formatoDeMonto = (monto: number | null | undefined): string =>
  monto === null || monto === undefined
    ? SIN_DATO
    : monto.toLocaleString('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })

/** `$18,000 – $25,000`, o un guion si la vacante no trae rango. */
export const textoDelRango = (
  rango: CandidatoParaDireccion['hrSalaryRange'],
): string => {
  if (!rango || (rango.min === null && rango.max === null)) return SIN_DATO
  return `${formatoDeMonto(rango.min)} – ${formatoDeMonto(rango.max)}`
}

/** La calificación de RH sobre diez, o un guion si no hubo entrevista. */
export const textoDeCalificacion = (
  entrevista: CandidatoParaDireccion['hrInterview'],
): string =>
  sinEntrevistaDeRh(entrevista) || entrevista?.score === null
    ? SIN_DATO
    : `${entrevista!.score}/10`

/**
 * Una entrevista sin resultado y sin cancelar sigue esperando que alguien la
 * registre. Las canceladas ya no (SRH-312): Dirección decidió sin ellas.
 */
export const esperaResultado = (entrevista: EntrevistaDeDireccion): boolean =>
  entrevista.result === null && !entrevista.cancelledAt

/**
 * Las entrevistas que se van a cancelar si Dirección decide ahora (SRH-312).
 *
 * Al registrar el resultado de una entrevista, esa no cuenta: es la que decide.
 */
export const entrevistasPendientes = (
  entrevistas: EntrevistaDeDireccion[],
  exceptoId?: number,
): EntrevistaDeDireccion[] =>
  entrevistas.filter((e) => e.id !== exceptoId && esperaResultado(e))

/** La advertencia al decidir con entrevistas sin resultado; vacía si no hay. */
export const avisoDeEntrevistasPendientes = (
  pendientes: EntrevistaDeDireccion[],
): string => {
  if (pendientes.length === 0) return ''
  const cuando = pendientes
    .map((e) => `${formatDate(e.interviewDate)} ${e.interviewTime}`)
    .join(', ')
  return pendientes.length === 1
    ? `Este candidato tiene una entrevista de Dirección sin resultado (${cuando}). Al decidir se cancela.`
    : `Este candidato tiene ${pendientes.length} entrevistas de Dirección sin resultado (${cuando}). Al decidir se cancelan.`
}

const FORMATO_DE_HORA = new Intl.DateTimeFormat('en-GB', {
  timeZone: ZONA_DE_OPERACION,
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
})
const FORMATO_DE_DIA = new Intl.DateTimeFormat('en-CA', {
  timeZone: ZONA_DE_OPERACION,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

/** Hoy en la zona de la operación, como `YYYY-MM-DD`. Sirve de mínimo del calendario. */
export const hoyEnLaOperacion = (ahora: Date = new Date()): string =>
  FORMATO_DE_DIA.format(ahora)

/**
 * Si la fecha y la hora que se capturaron ya pasaron (SRH-312).
 *
 * Se compara en la zona de la operación y como texto, igual que el servidor,
 * para que los dos digan lo mismo: el navegador puede estar en otra zona. Estar
 * en el minuto actual no cuenta como pasado. Sin fecha u hora no hay nada que
 * juzgar todavía.
 */
export const fechaHoraYaPaso = (
  fecha: string,
  hora: string,
  ahora: Date = new Date(),
): boolean => {
  if (!fecha || !hora) return false
  const pedida = `${fecha.slice(0, 10)}T${hora.padStart(5, '0')}`
  const actual = `${hoyEnLaOperacion(ahora)}T${FORMATO_DE_HORA.format(ahora)}`
  return pedida < actual
}

/**
 * Cómo se lee una entrevista en la lista de la ficha.
 *
 * La fecha va en dd/mm/yyyy, como el resto de RH: `interviewDate` ya viene como
 * día de calendario, y `formatDate` lo lee sin pasar por `new Date()`, que lo
 * correría un día.
 */
export const textoDeEntrevista = (entrevista: EntrevistaDeDireccion): string => {
  const cuando = `${formatDate(entrevista.interviewDate)} · ${entrevista.interviewTime}`
  const como = entrevista.modality === 'virtual' ? 'Virtual' : 'Presencial'
  const quien = entrevista.interviewer?.name
  return quien ? `${cuando} · ${como} · ${quien}` : `${cuando} · ${como}`
}

/** Etiqueta del resultado, para la pastilla de la entrevista. */
export const etiquetaDeResultado = (
  entrevista: EntrevistaDeDireccion,
): { texto: string; tono: 'pendiente' | 'aprobado' | 'rechazado' | 'cancelada' } => {
  // Una entrevista con resultado nunca se cancela; el resultado manda.
  if (entrevista.result === null && entrevista.cancelledAt) {
    return { texto: 'Cancelada', tono: 'cancelada' }
  }
  if (entrevista.result === 'approved') {
    return { texto: 'Aprobada', tono: 'aprobado' }
  }
  if (entrevista.result === 'rejected') {
    return { texto: 'Rechazada', tono: 'rechazado' }
  }
  return { texto: 'Pendiente de resultado', tono: 'pendiente' }
}

/**
 * Agendar queda bloqueado en cuanto una entrevista decidió.
 *
 * El primer resultado cierra el proceso, así que una entrevista agendada
 * después nacería muerta: el back la rechazaría y, peor, en la pantalla
 * parecería que el candidato sigue en juego.
 */
export const yaSeDecidio = (entrevistas: EntrevistaDeDireccion[]): boolean =>
  entrevistas.some((e) => e.result !== null)

/** Mensaje del error, con el 409 dicho en claro. */
export const mensajeDeFallo = (error: unknown): string => {
  const e = error as { statusCode?: number; status?: number; message?: string }
  const codigo = e?.statusCode ?? e?.status

  if (codigo === 409) {
    return 'Ya se tomó una decisión sobre este candidato. Se actualizó la lista.'
  }

  return e?.message || 'No se pudo completar la acción. Intenta de nuevo.'
}
